import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type { CostSnapshot, Decoration, OrderTaxRecords, PricingSnapshot } from './types';

/**
 * Pricing-service request descriptors, exported separately so the wire-shape
 * lock test asserts URL/method without the network layer (mirrors
 * `phase5RequestBuilders` / `contactVendorRequestBuilders` in the integration
 * api). Live wire verified against UAT:
 *
 *   GET /pricing/v1/orders/{orderId}        → order pricing snapshot
 *   GET /pricing/v1/shipments/{shipmentId}  → per-shipment cost snapshot
 *   GET /pricing/v1/taxes/orders/{orderId}  → the order's tax records + totals
 *
 * Tenant scope rides the X-Access-IDs header that `portalBaseQuery` attaches —
 * no extra params.
 */
export const pricingRequestBuilders = {
  getOrderPricingSnapshot: (orderId: string): { url: string; method: 'GET' } => ({
    url: `${services.pricing}/v1/orders/${encodeURIComponent(orderId)}`,
    method: 'GET',
  }),
  getShipmentCostSnapshot: (shipmentId: string): { url: string; method: 'GET' } => ({
    url: `${services.pricing}/v1/shipments/${encodeURIComponent(shipmentId)}`,
    method: 'GET',
  }),
  getOrderTaxRecords: (orderId: string): { url: string; method: 'GET' } => ({
    url: `${services.pricing}/v1/taxes/orders/${encodeURIComponent(orderId)}`,
    method: 'GET',
  }),
};

// The pricing service pre-sums decorations into `decorationPrice`; `decorations[]` is always empty on the wire.
// `sumDecorations` is a fallback for any response that omits the pre-summed field.
function sumDecorations(decorations: Decoration[] | undefined): { amount: number; currency: string } | undefined {
  if (!decorations || decorations.length === 0) return undefined;
  const currency = decorations.find((d) => d.price?.currency)?.price?.currency;
  if (!currency) return undefined;
  return { amount: decorations.reduce((sum, d) => sum + (d.price?.amount ?? 0), 0), currency };
}

export const pricingApi = createApi({
  reducerPath: 'pricingApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['OrderPricingSnapshot', 'ShipmentCostSnapshot', 'OrderTaxRecords'],
  endpoints: (builder) => ({
    /**
     * The service returns the snapshot flat (no envelope observed in the legacy
     * consumer). `transformResponse` is implemented explicitly so it's the one
     * place to unwrap/rename if the live wire differs (see types.ts caveat).
     * A 404 (no snapshot for this order) surfaces as the hook's `error`; the
     * panel treats "no data" as the empty state, not a hard failure.
     */
    getOrderPricingSnapshot: builder.query<PricingSnapshot, string>({
      query: (orderId) => pricingRequestBuilders.getOrderPricingSnapshot(orderId),
      transformResponse: (response: PricingSnapshot): PricingSnapshot => ({
        ...response,
        items: response.items?.map((item) => ({
          ...item,
          decorationPrice: item.decorationPrice ?? sumDecorations(item.decorations),
        })),
      }),
      providesTags: (_r, _e, orderId) => [{ type: 'OrderPricingSnapshot', id: orderId }],
    }),
    getShipmentCostSnapshot: builder.query<CostSnapshot, string>({
      query: (shipmentId) => pricingRequestBuilders.getShipmentCostSnapshot(shipmentId),
      transformResponse: (response: CostSnapshot): CostSnapshot => response,
      providesTags: (_r, _e, shipmentId) => [{ type: 'ShipmentCostSnapshot', id: shipmentId }],
    }),
    /**
     * The order's tax records with the totals summed by the service. An order
     * without records answers 200 with an empty list, so "no tax yet" is data,
     * not an error.
     */
    getOrderTaxRecords: builder.query<OrderTaxRecords, string>({
      query: (orderId) => pricingRequestBuilders.getOrderTaxRecords(orderId),
      transformResponse: (response: OrderTaxRecords): OrderTaxRecords => response,
      providesTags: (_r, _e, orderId) => [{ type: 'OrderTaxRecords', id: orderId }],
    }),
  }),
});

export const {
  useGetOrderPricingSnapshotQuery,
  useGetShipmentCostSnapshotQuery,
  useGetOrderTaxRecordsQuery,
} = pricingApi;
